import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  CartesianGrid,
  Label,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { Tooltip } from "recharts";
import { Line } from "recharts";
import { Cell } from "recharts";

const StudentRankingCharts = ({ rankingData }) => {
  // const { myRanking } = useSelector((state) => state.ranking);

  // const rankingData = myRanking.ranking;

  const overallScore = Math.round(rankingData.totalScore || 0);
  const trendData = [
    { week: "Week 1", score: 40 },
    { week: "Week 2", score: 42 },
    { week: "Week 3", score: 43 },
    { week: "Week 4", score: 44 },
    { week: "Week 5", score: 45 },
    { week: "Week 6", score: Math.round(rankingData.totalScore || 0) },
  ];

  const performanceData = [
    {
      name: "Attendance",
      value: Math.round(rankingData.attendanceScore || 0),
      color: "#6366f1",
    },
    {
      name: "Interviews",
      value: Math.round(rankingData.interviewScore || 0),
      color: "#8b5cf6",
    },
    {
      name: "Assignments",
      value: Math.round(rankingData.assignmentScore || 0),
      color: "#16A34A",
    },
    {
      name: "Daily Tasks",
      value: Math.round(rankingData.taskScore || 0),
      color: "#f59e0b",
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 shadow-sm gap-6">
      {/* Performance Trend */}
      <Card className="lg:col-span-2 bg-white border-0 shadow-md">
        <CardHeader className="border-b border-gray-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-foreground">
              Performance Trend
            </CardTitle>
            <div className="flex items-center gap-2 text-sm dark:text-foreground text-gray-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                <span>Score Progress</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--grid-color, #f0f0f0)"
                className="dark:[--grid-color:#374151]" // dark: gray-700
              />
              <XAxis
                dataKey="week"
                tick={{
                  fill: "var(--axis-color, #6b7280)",
                  fontSize: 12,
                }}
                axisLine={{
                  stroke: "var(--axis-line, #e5e7eb)",
                }}
                className="dark:[--axis-color:#d1d5db] dark:[--axis-line:#374151]" // dark: gray-300 / gray-700
              />
              <YAxis
                tick={{
                  fill: "var(--axis-color, #6b7280)",
                  fontSize: 12,
                }}
                axisLine={{
                  stroke: "var(--axis-line, #e5e7eb)",
                }}
                className="dark:[--axis-color:#d1d5db] dark:[--axis-line:#374151]"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--tooltip-bg, white)",
                  border: "1px solid var(--tooltip-border, #e5e7eb)",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                }}
                itemStyle={{
                  color: "var(--tooltip-text, #111827)",
                }}
                wrapperClassName="dark:[--tooltip-bg:#1f2937] dark:[--tooltip-border:#374151] dark:[--tooltip-text:#e5e7eb]"
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="var(--line-color, #6366f1)"
                strokeWidth={3}
                dot={{ fill: "var(--line-color, #6366f1)", r: 5 }}
                activeDot={{ r: 7 }}
                className="dark:[--line-color:#818cf8]" // lighter indigo for dark mode
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Distribution Pie Chart */}
      <Card className="bg-white border-0 shadow-md">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-lg font-semibold text-foreground">
            Score Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={performanceData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
                paddingAngle={2}
              >
                {performanceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <Label
                  value={`${overallScore}%`}
                  position="center"
                  fill="var(--label-color, #16A34A)"
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                  className="dark:[--label-color:#4ade80]" // lighter red in dark mode
                />
              </Pie>

              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--tooltip-bg, white)",
                  border: "1px solid var(--tooltip-border, #e5e7eb)",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                }}
                itemStyle={{
                  color: "var(--tooltip-text, #111827)",
                }}
                wrapperClassName="dark:[--tooltip-bg:#1f2937] dark:[--tooltip-border:#374151] dark:[--tooltip-text:#e5e7eb]"
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="space-y-2 mt-4">
            {performanceData.map((item) => (
              <div
                key={item.name}
                className="flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-gray-600 dark:text-gray-300">
                    {item.name}
                  </span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {item.value}%
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentRankingCharts;
